import * as React from 'react';
import classnames from 'classnames';
import { isNil } from 'lodash';

import SVGIcon from 'components/svg-icon/SVGIcon';
import Tooltip from 'components/Tooltip';
import Popover from 'components/popover/Popover';

import { __t, applyTranslations } from 'utils/language';
import { computeDisplayedMetricValue } from 'utils/helper/metrics';
import { isMetricsStillFetching } from 'utils/helper/profile';
import { getFormattedFollowersValue } from 'utils/influencers';
import { getExternalUrl } from 'utils/url';
import { getFormattedPercentageRate } from 'utils/formatters';
import {
  CHANNEL_TYPE_INSTAGRAM,
  CHANNEL_TYPE_TIKTOK,
  CHANNEL_TYPE_YOUTUBE,
  getUserNameFromChannel,
  isChannelHasEnoughFollowers
} from 'utils/channels';

import './influencer-profile-summary-channels.scss';

import type { Channel, Metrics } from 'types';

applyTranslations(require('locales/common/collabary-channels.i18n.json'));
applyTranslations(require('./influencer-profile-summary-channels.i18n.json'));

type SectionType = 'profile' | 'additional';
type HeaderItemType = 'channel' | 'followers' | 'engagement' | 'additional';
type NumString = number | string;

interface Props {
  channels: Array<Channel>,
  type: SectionType
}

const SUPPORTED_CHANNELS = [
  CHANNEL_TYPE_INSTAGRAM,
  CHANNEL_TYPE_TIKTOK,
  CHANNEL_TYPE_YOUTUBE
];

class InfluencerProfileSummaryChannels extends React.PureComponent<Props> {
  getClassname(identifier: string, type?: string) {
    return classnames({
      [`influencer-profile-summary-channels__${identifier}`]: Boolean(identifier),
      [`influencer-profile-summary-channels__${identifier}--type-${String(type)}`]: Boolean(type)
    });
  }

  getDisplayValue(
    value: NumString | undefined | null,
    key: string,
    defaultValue: string | number
  ): NumString {
    return computeDisplayedMetricValue(
      value,
      key,
      defaultValue
    );
  }

  getMetricValue(metrics: Metrics | undefined | null, key: string): NumString | undefined | null {
    if (key === 'followers') {
      const value = metrics ? getFormattedFollowersValue(metrics[key]) : null;
      return this.getDisplayValue(value, key, 0);
    }

    if (key === 'engagementRate') {
      const value = metrics ? metrics[key] : null;
      return getFormattedPercentageRate(value);
    }

    return null;
  }

  renderPopoverItemLabel(itemType: HeaderItemType): React.ReactNode {
    return (
      <div>
        {__t(`influencer_profile_summary_channels.label_${itemType}`)}:
      </div>
    );
  }

  renderIcon(channel: Channel): React.ReactNode {
    const userName = getUserNameFromChannel(channel);
    const userNameLabel = userName ? `<br>@${userName}` : '';
    const tooltipNameLabel = userNameLabel ? `:${userNameLabel}` : userNameLabel;

    return (
      <Tooltip
        key={channel.type}
        message={`${__t(`collabary_channels.${channel.type}.label`)}${tooltipNameLabel}`}
        width="auto-compact"
      >
        <SVGIcon
          name={`${channel.type}-no-customisation`}
          width={24}
          height={24}
          link={getExternalUrl(channel.url || channel.urlRaw)} // `urlRaw` = tiktok default url, not custom FE version
          className={this.getClassname('icon')}
        />
      </Tooltip>
    );
  }

  renderInfo(channel?: Channel | null): React.ReactNode | undefined | null {
    if (isNil(channel)) {
      return null;
    }
    const userName = getUserNameFromChannel(channel);
    const userNameLabel = userName ? `@${userName}` : '';

    return (
      <div
        key={`influencer-profile-summary-channels__body-item-${channel.type}-${channel.id}`}
        className={this.getClassname('item-container')}
      >
        <div className={`${this.getClassname('item')} ${this.getClassname('channel-icon')}`}>
          {this.renderIcon(channel)}
        </div>
        <Popover
          triggerComponent={(
            <React.Fragment>
              <div className={this.getClassname('item')}>
                {
                  // COLLAB-14071 & COLLAB-17556
                  !window.app.env.FEATURE_FACEBOOK_LOGIN
                  && this.getMetricValue(channel.metrics, 'followers')
                }
              </div>
              {
                  // COLLAB-14071 & COLLAB-17556
                  !window.app.env.FEATURE_FACEBOOK_LOGIN
                  && (
                    <div className={`${this.getClassname('item')} ${this.getClassname('divider')}`}>
                      |
                    </div>
                  )
                }
              <div className={this.getClassname('item')}>
                {this.getMetricValue(channel.metrics, 'engagementRate')}
              </div>
            </React.Fragment>
          )}
          classNameWrapper="influencer-profile-summary-channels__metric-popover"
          classNamePopover="influencer-profile-summary-channels__metric-popover-content"
        >
          <div>
            <div className={this.getClassname('popover-header')}>
              <SVGIcon
                name={`${channel.type}-no-customisation`}
                width={24}
                height={24}
                className={this.getClassname('icon')}
              />
              <div className={this.getClassname('popover-header-name')}>{userNameLabel}</div>
            </div>
            {
              // COLLAB-14071 & COLLAB-17556
              !window.app.env.FEATURE_FACEBOOK_LOGIN
              && (
                <div className={this.getClassname('popover-item')}>
                  {this.renderPopoverItemLabel('followers')}
                  <div className={this.getClassname('popover-item-value')}>
                    {this.getMetricValue(channel.metrics, 'followers')}
                  </div>
                </div>
              )
            }
            <div className={this.getClassname('popover-item')}>
              {this.renderPopoverItemLabel('engagement')}
              <div className={this.getClassname('popover-item-value')}>
                {this.getMetricValue(channel.metrics, 'engagementRate')}
              </div>
            </div>
          </div>
        </Popover>
      </div>
    );
  }

  renderChannel(channel?: Channel | null): React.ReactNode | undefined | null {
    if (isNil(channel)) {
      return null;
    }

    return (
      <div
        key={`influencer-profile-summary-channels__body-item-${channel.type}-${channel.id}`}
        className={this.getClassname('item-container')}
      >
        <div className={`${this.getClassname('item')} ${this.getClassname('channel-icon')}`}>
          {this.renderIcon(channel)}
        </div>
      </div>
    );
  }

  renderProfileChannels(): React.ReactNode {
    const { channels } = this.props;

    return (
      <div className={this.getClassname('body', 'profile')}>
        {
          SUPPORTED_CHANNELS.map(channel => (
            this.renderInfo(
              channels
                .filter(ch => isChannelHasEnoughFollowers(ch)
                  // COLLAB-13712 & COLLAB-17556
                  || window.app.env.FEATURE_FACEBOOK_LOGIN
                )
                .find(ch => ch.type === channel)
            )
          ))
          }
      </div>
    );
  }

  renderAdditionalChannels(): React.ReactNode {
    const { channels } = this.props;

    return (
      <div className={this.getClassname('body', 'additional')}>
        {
          channels
            .filter(
              channel => !SUPPORTED_CHANNELS.includes(channel.type)
                || isMetricsStillFetching(channel.metrics)
                || !isChannelHasEnoughFollowers(channel)
            )
            .map(channel => (
              this.renderChannel(channel)
            ))
          }
      </div>
    );
  }

  render(): React.ReactNode {
    const { type } = this.props;

    return (
      <div className="influencer-profile-summary-channels">
        {
          type === 'additional'
            ? this.renderAdditionalChannels()
            : this.renderProfileChannels()
        }
      </div>
    );
  }
}

export default InfluencerProfileSummaryChannels;
